/**
 * Página "Escolher Estado" na Lacrei Saúde.
 * Permite que os profissionais selecionem o estado brasileiro em que atuam, como parte do processo de cadastro na plataforma.
 */

import { navigate } from 'gatsby';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { HealthProfessional2Img } from '../../assets/images';
import { Button, Dropdown } from '../../components/form';
import { Container, Footer, Header } from '../../components/shared';
import { DataUtilsContext } from '../../contexts/DataUtils';
import { LoginContext } from '../../contexts/Login';
import { isEmptyString } from '../../helpers/shared';
import { useServiceMethod } from '../../services/common/use-service-method';
import { AddressService } from '../../services/profissional/address';
import { FormWrapper, ImageWrapper, InputsWrapper, Main } from '../../styles/saude/cadastrar-estado';
import { statusActionProfessionalData } from '../../utils';

/**
 * Apresenta um dropdown para seleção do estado de atuação do profissional, com validação e redirecionamento conforme a escolha.
 * Inclui tratamento de erros e navegação para a próxima etapa do cadastro ou para a página de localidade indisponível.
 *
 * @returns {JSX.Element} - Um componente React representando a página de escolha do estado de atuação.
 */
export default function CriarConta() {
	/**
	 * Estados e hooks para gerenciamento da seleção de estado.
	 * - 'selectedValue': Armazena o estado escolhido pelo usuário.
	 * - 'isSelectActive': Controla a ativação do dropdown de seleção.
	 * - 'isError': Armazena mensagens de erro relacionadas à seleção.
	 * - 'statesData': Dados dos estados brasileiros para o dropdown.
	 */
	const [selectedValue, setSelectedValue] = useState('');
	const [isSelectActive, setIsSelectActive] = useState(false);
	const [isError, setIsError] = useState(null);
	const { statesData, setStatesData } = useContext(DataUtilsContext);
	const { professionalData } = useContext(LoginContext);
	/**
	 * 'address': Utiliza o hook 'useServiceMethod' para acessar o serviço 'AddressService' e obter a lista de estados.
	 * 'success': Verifica se a chamada para obter os estados foi bem-sucedida, checando o status e a existência da resposta.
	 */
	const address = useServiceMethod(AddressService, 'getStates');
	const success = address.status === 'success' && address.response;
	/**
	 * Função para formatar a lista de estados para uso no dropdown.
	 * Transforma os dados dos estados em um formato adequado para seleção, adicionando a opção 'Outros'.
	 * Memorizada com 'useCallback' para eficiência.
	 */
	const formatStates = useCallback((states) => {
		const statesList = [];

		states.map((state) => {
			statesList.push({
				id: state.id,
				value: state.code,
				text: state.name,
			});
		});

		statesList.push({ value: 'others', text: 'Outros' });

		return statesList;
	}, []);

	/**
	 * Efeito para fazer a chamada ao serviço de endereço e obter os estados.
	 * Executa a função 'call' do serviço de endereço para buscar dados dos estados.
	 * É executado apenas uma vez após o montagem inicial do componente.
	 */
	useEffect(async () => {
		await address.call(true);
	}, []);

	/**
	 * Efeito para atualizar os dados dos estados.
	 * Atualiza o contexto 'DataUtilsContext' com os estados formatados, uma vez que a chamada ao serviço de endereço é bem-sucedida.
	 * Dependente da variável 'success' para reagir apenas quando a chamada ao serviço de endereço é bem-sucedida.
	 */
	useEffect(() => {
		if (success) {
			setStatesData({
				...statesData,
				items: formatStates(address.response),
			});
		}
	}, [success]);

	/**
	 * useEffect para tratamento de erros na chamada ao serviço de endereço.
	 * Define uma mensagem de erro se a chamada ao serviço de endereço falhar ou for rejeitada.
	 * Dependente da variável 'address.status' para reagir a mudanças no status da chamada ao serviço de endereço.
	 */
	useEffect(() => {
		if (address.status === 'excepted' || address.status === 'failure') {
			setIsError('Houve um erro ao buscar os estados. Tente novamente mais tarde.');
		}

		/**
		 * Realiza uma verificação para mostrar os estados caso houver uma falha.
		 *
		 * @throws {Error} Lança um erro caso o status do endereço ocorrer uma falha.
		 */
	}, [address.status]);

	/**
	 *
	 * @param {*} value Recebe como parametro o valor que o usuário escolheu
	 *
	 * @remarks
	 * Está função salva o estado que o usuário clicou.
	 */
	function handleSelectedOption(value) {
		setSelectedValue(value.text);
		setIsSelectActive(false);
	}

	const handleOpenSelect = useCallback(() => {
		setIsSelectActive(!isSelectActive);
		setIsError(null);
	}, [selectedValue, isSelectActive]);

	/**
	 * Realiza o envio do estado que o usuário escolheu.
	 *
	 * @param {*} e Recebe como parametro o event para previnir o comportamento
	 *
	 * @remarks
	 * Está função tenta realizar o envio do formulário contendo o estado escolhido.
	 * Se nao for bem sucedida, exibe uma mensagem de erro para estado obrigatório.
	 * Se o escolhido for Outros, redireciona para localidade indisponivel.
	 * Se for bem sucedida, redireciona para o pré cadastro.
	 */
	const handleOnSubmit = useCallback(
		(e) => {
			const invalid = isEmptyString(selectedValue);
			const stateID = statesData.items.find((option) => option.text === selectedValue);

			if (invalid) {
				e.preventDefault();
				setIsError('O campo “Estado” é obrigatório.');
				return;
			}

			if (selectedValue !== 'Outros') {
				navigate('/saude/pre-cadastro/', { state: { stateID } });
			} else if (selectedValue === 'Outros') {
				navigate('/saude/localidade-indisponivel');
			}
		},
		[selectedValue],
	);

	/**
	 * Possui o fluxo de fazer uma verificação de segurança, de que se o usuário tiver realizado o login, e o token estiver armazenado no SessionStorage, ele não pode acessar diretamente esta página.
	 * Para acessar, é necessário fazer o logout.
	 */
	useEffect(() => {
		const authToken = sessionStorage.getItem('authToken');
		if (authToken) navigate(statusActionProfessionalData[professionalData.status]);
	});

	return (
		<>
			<Helmet>
				<title>Escolher Estado | Lacrei Saúde</title>
				<meta
					name='description'
					content='Pagina com a opção do profissional escolher o estado 
                    brasileiro em que atua para poder prosseguir com o cadastro'
				/>
			</Helmet>
			<Container>
				<Header />
				<Main>
					<FormWrapper>
						<h1>Informe o Estado onde você atua</h1>
						<InputsWrapper>
							<Dropdown
								data={statesData}
								selectedValue={selectedValue}
								onSelectOption={handleSelectedOption}
								isSelectActive={isSelectActive}
								onOpenSelect={handleOpenSelect}
								onChange={(event) => setSelectedValue(event.target.value)}
								isError={isError}
								isOverflowed
							/>
							<Button title='Próximo' onClick={handleOnSubmit} shadowy />
							<Button title='Voltar' link='/' shadowy isVariant />
						</InputsWrapper>
					</FormWrapper>
					<ImageWrapper>
						<HealthProfessional2Img
							role='img'
							alt='Pessoa ao lado de um marcador de localização.'
							aria-label='Pessoa ao lado de um marcador de localização.'
							className='image'
						/>
						<span className='image-credits'>
							Ilustrado por{' '}
							<a className='anchor-image' href='https://br.freepik.com/'>
								Freepik
							</a>
						</span>
					</ImageWrapper>
				</Main>
				<Footer />
			</Container>
		</>
	);
}
